import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Check, CircleDot } from "lucide-react";
import PageHero from "@/components/site/PageHero";
import MagneticButton from "@/components/site/MagneticButton";
import ExitIntentModal from "@/components/site/ExitIntentModal";
import { getService, getServices, getInsights } from "@/lib/api";
import { useSEO } from "@/lib/seo";

const EASE = [0.22, 1, 0.36, 1];

const PROCESS = [
  { label: "Discover", desc: "Deep-dive assessment, telemetry sampling, and interviews with your team." },
  { label: "Design", desc: "Target-state architecture and prioritised roadmap with business impact." },
  { label: "Deploy", desc: "Tooling stood up, detections engineered, playbooks operationalised." },
  { label: "Operate & Improve", desc: "Named team, signed SLAs, and continuous uplift baked into cadence." },
];

const TOC = [
  { id: "overview", label: "Overview" },
  { id: "capabilities", label: "Capabilities" },
  { id: "process", label: "How we deliver" },
  { id: "outcomes", label: "Outcomes" },
  { id: "related", label: "Related" },
];

export default function ServiceDetail() {
  const { slug } = useParams();
  const [svc, setSvc] = useState(null);
  const [all, setAll] = useState([]);
  const [related, setRelated] = useState([]);
  const [insights, setInsights] = useState([]);
  const [active, setActive] = useState("overview");
  const reduced = useReducedMotion();

  useEffect(() => {
    setSvc(null);
    getService(slug).then(setSvc).catch(() => setSvc({ notFound: true }));
    getServices().then(setAll).catch(() => {});
    getInsights({ limit: 3 })
      .then((d) => setInsights(d.items || []))
      .catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (svc && svc.related_services && all.length) {
      setRelated(all.filter((s) => svc.related_services.includes(s.slug)));
    }
  }, [svc, all]);

  useEffect(() => {
    const onScroll = () => {
      for (const t of [...TOC].reverse()) {
        const el = document.getElementById(t.id);
        if (el && el.getBoundingClientRect().top < 200) {
          setActive(t.id);
          return;
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [slug]);

  useSEO({
    title: svc?.seo_title || (svc?.name ? `${svc.name} | RockLadder` : "Service"),
    description: svc?.seo_description || svc?.short_desc,
    canonical: `https://rockladder.com/services/${slug}`,
    jsonLd: svc && !svc.notFound && {
      "@context": "https://schema.org",
      "@graph": [
        {
          "@type": "Service",
          name: svc.name,
          description: svc.short_desc,
          provider: { "@type": "Organization", name: "RockLadder Technologies" },
        },
        {
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://rockladder.com/" },
            { "@type": "ListItem", position: 2, name: "Services", item: "https://rockladder.com/services" },
            { "@type": "ListItem", position: 3, name: svc.name, item: `https://rockladder.com/services/${slug}` },
          ],
        },
      ],
    },
  });

  if (svc?.notFound) {
    return (
      <div className="pt-40 pb-24 text-center">
        <p className="text-inkText-muted">Service not found.</p>
        <Link to="/services" className="text-gold hover:underline">Back to Services</Link>
      </div>
    );
  }
  if (!svc) return <div className="pt-40 pb-24 text-center text-inkText-muted">Loading…</div>;

  return (
    <>
      <PageHero
        eyebrow={`SERVICE · ${String(svc.order || "").padStart(2, "0")}`}
        title={svc.name}
        subtitle={svc.tagline}
        breadcrumb={[{ label: "Services", to: "/services" }, { label: svc.name }]}
      />

      <section className="relative py-12 md:py-16">
        <div className="mx-auto max-w-[1440px] px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-[220px_1fr] gap-10">
          {/* Sticky TOC */}
          <aside className="hidden lg:block">
            <div className="sticky top-28">
              <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-4">ON THIS PAGE</div>
              <ul className="space-y-2">
                {TOC.map((t) => (
                  <li key={t.id}>
                    <a
                      href={`#${t.id}`}
                      data-testid={`toc-${t.id}`}
                      className={`inline-flex items-center gap-2 text-sm transition-colors ${
                        active === t.id
                          ? "text-gold"
                          : "text-inkText-muted hover:text-inkText-primary"
                      }`}
                    >
                      <CircleDot
                        className={`h-3 w-3 ${active === t.id ? "opacity-100" : "opacity-40"}`}
                        strokeWidth={2}
                      />
                      {t.label}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          {/* Content */}
          <div className="max-w-3xl">
            <section id="overview" data-testid="section-overview">
              <div className="prose prose-invert max-w-none prose-p:text-inkText-muted prose-p:leading-[1.75] prose-headings:font-display prose-headings:text-inkText-primary prose-strong:text-inkText-primary">
                {svc.long_description_md
                  ?.split("\n\n")
                  .map((block, i) => {
                    const b = block.trim();
                    if (!b) return null;
                    if (b.startsWith("## ")) {
                      return <h2 key={i} className="mt-10 first:mt-0 text-2xl md:text-3xl">{b.slice(3)}</h2>;
                    }
                    if (b.startsWith("### ")) {
                      return <h3 key={i} className="mt-8 text-lg md:text-xl">{b.slice(4)}</h3>;
                    }
                    if (b.startsWith("- ")) {
                      return (
                        <ul key={i} className="my-4 space-y-2 list-none pl-0">
                          {b.split("\n").map((line, j) => (
                            <li key={j} className="flex items-start gap-2 text-inkText-muted">
                              <Check className="h-4 w-4 mt-1 text-gold flex-shrink-0" strokeWidth={2.4} />
                              <span>{line.replace(/^-\s*/, "")}</span>
                            </li>
                          ))}
                        </ul>
                      );
                    }
                    return <p key={i}>{b}</p>;
                  })}
              </div>
            </section>

            <section id="capabilities" data-testid="section-capabilities" className="mt-16">
              <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-6">Capabilities</h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {(svc.capabilities || []).map((c, i) => (
                  <motion.li
                    key={c}
                    initial={reduced ? false : { opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.03, ease: EASE }}
                    className="flex items-start gap-3 glass-panel rounded-xl p-4 text-sm text-inkText-primary/90"
                  >
                    <Check className="h-4 w-4 mt-0.5 text-gold flex-shrink-0" strokeWidth={2.4} />
                    <span>{c}</span>
                  </motion.li>
                ))}
              </ul>
              {svc.deliverables?.length > 0 && (
                <div className="mt-10">
                  <h3 className="font-display text-lg text-inkText-primary mb-4">What you receive</h3>
                  <ul className="space-y-2">
                    {svc.deliverables.map((d) => (
                      <li key={d} className="flex items-start gap-3 text-sm text-inkText-muted">
                        <span className="mt-1.5 h-1.5 w-1.5 rounded-full bg-gold flex-shrink-0" />
                        <span>{d}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>

            <section id="process" data-testid="section-process" className="mt-16">
              <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-6">How we deliver</h2>
              <ol className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {PROCESS.map((p, i) => (
                  <li key={p.label} className="relative glass-panel rounded-xl p-5">
                    <div className="font-mono text-[10px] tracking-[0.24em] text-gold-body mb-2">
                      STEP · {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="font-display text-lg text-inkText-primary">{p.label}</div>
                    <p className="mt-2 text-sm text-inkText-muted leading-relaxed">{p.desc}</p>
                  </li>
                ))}
              </ol>
            </section>

            {svc.outcomes?.length > 0 && (
              <section id="outcomes" data-testid="section-outcomes" className="mt-16">
                <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-6">Outcomes</h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  {svc.outcomes.map((o) => (
                    <div key={o.label} className="glass-panel rounded-xl p-5 text-center">
                      <div className="font-display text-3xl md:text-4xl text-gradient-gold">
                        {o.value}
                      </div>
                      <div className="mt-3 font-mono text-[10px] tracking-[0.24em] text-inkText-muted">
                        {o.label.toUpperCase()}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* Mid-page CTA */}
            <div className="mt-16 rounded-2xl glass-panel-strong p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center gap-4 justify-between">
              <div>
                <div className="font-display text-lg md:text-xl text-inkText-primary">
                  Ready to see this in your environment?
                </div>
                <p className="text-sm text-inkText-muted mt-1">
                  30-min working session with a senior engineer. No slides.
                </p>
              </div>
              <MagneticButton as={Link} to="/contact" variant="primary" data-testid="service-mid-cta">
                Book a Consultation <ArrowRight className="h-4 w-4" />
              </MagneticButton>
            </div>

            <section id="related" data-testid="section-related" className="mt-16">
              <h2 className="font-display text-2xl md:text-3xl text-inkText-primary mb-6">Related services</h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/services/${r.slug}`}
                    className="glass-panel rounded-xl p-5 group hover:border-gold transition-colors"
                    data-testid={`related-service-${r.slug}`}
                  >
                    <div className="font-display text-base text-inkText-primary group-hover:text-gold">
                      {r.name}
                    </div>
                    <p className="mt-2 text-xs text-inkText-muted line-clamp-2">{r.tagline}</p>
                    <div className="mt-3 inline-flex items-center gap-1 font-mono text-[10px] tracking-widest text-gold-body">
                      READ <ArrowUpRight className="h-3 w-3" />
                    </div>
                  </Link>
                ))}
              </div>

              {insights.length > 0 && (
                <div className="mt-12">
                  <h3 className="font-display text-lg text-inkText-primary mb-4">
                    Related insights
                  </h3>
                  <ul className="space-y-3">
                    {insights.map((i) => (
                      <li key={i.slug}>
                        <Link
                          to={`/insights/${i.slug}`}
                          className="flex items-center justify-between glass-panel rounded-lg p-4 hover:border-gold transition-colors group"
                        >
                          <span className="text-sm text-inkText-primary group-hover:text-gold">
                            {i.title}
                          </span>
                          <ArrowUpRight className="h-3.5 w-3.5 text-inkText-muted group-hover:text-gold" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          </div>
        </div>
      </section>

      {/* Exit-intent CTA modal */}
      <ExitIntentModal
        serviceName={svc.name}
        sourcePage={`/services/${slug}`}
      />
    </>
  );
}
